import { useState, useRef, useImperativeHandle, RefObject } from 'react';

interface InputRef<T> {
  getCurrentValue: () => T | null | string;
  clear: () => void;
}

interface UseMergePendingItemsOptions<T, U> {
  items: T[];
  updateItems: (items: T[]) => void;
  inputRef: RefObject<InputRef<U>>;
  transformItem?: (item: U | string) => T;
  mergeOrder?: 'prepend' | 'append';
  currentValuePosition?: 'first' | 'last';
  isDuplicate?: (item: T, list: T[]) => boolean;
}

export function useMergePendingItems<T, U = string>(
  options: UseMergePendingItemsOptions<T, U>
) {
  const { items, updateItems, inputRef, transformItem, mergeOrder = 'prepend', currentValuePosition = 'last', isDuplicate } = options;
  const [pendingItems, setPendingItems] = useState<U[]>([]);

  const mergePendingItems = (): T[] => {
    const currentValue = inputRef.current?.getCurrentValue();
    const allPendingItems = currentValue 
      ? (currentValuePosition === 'first' ? [currentValue, ...pendingItems] : [...pendingItems, currentValue])
      : pendingItems;
    
    if (allPendingItems.length > 0) {
      const transformedItems = transformItem
        ? allPendingItems.map(item => transformItem(item))
        : (allPendingItems as unknown as T[]);
      
      const uniqueTransformedItems = isDuplicate
        ? transformedItems.filter(item => !isDuplicate(item, items))
        : transformedItems;
      
      const merged = mergeOrder === 'prepend' 
        ? [...uniqueTransformedItems, ...items]
        : [...items, ...uniqueTransformedItems];
      
      updateItems(merged);
      setPendingItems([]);
      inputRef.current?.clear();
      return merged;
    }
    return items;
  };

  const addToPending = (item: U) => {
    setPendingItems([...pendingItems, item]);
  };

  const clearPending = () => {
    setPendingItems([]);
  };

  return {
    pendingItems,
    mergePendingItems,
    addToPending,
    clearPending,
    setPendingItems,
  };
}

export function useMergePendingItemsHandle<T>(
  ref: React.Ref<{ mergePendingItems: () => T[] }>,
  mergePendingItems: () => T[]
) {
  useImperativeHandle(ref, () => ({
    mergePendingItems,
  }));
}

